import { distanceM } from './geo';
import type { Network } from './operators';

export type Radio = 'GSM' | 'UMTS' | 'LTE' | 'NR';
export type Reliability = 'verificato' | 'manuale' | 'stimato';

export type Cell = { network: Network; radio: Radio; lat: number; lon: number; samples: number; rangeM: number };

export type Site = {
  id: string;
  network: Network;
  lat: number;
  lon: number;
  reliability: Reliability;
  radios: string[];
  name?: string;
  note?: string;
  source?: string;
  checkedAt?: string;
};

const RADIO_ORDER = ['GSM', 'UMTS', 'LTE', 'NR'];
const sortRadios = (r: Iterable<string>) => [...new Set(r)].sort((a, b) => RADIO_ORDER.indexOf(a) - RADIO_ORDER.indexOf(b));

/** Greedy per-network clustering: each cell joins the first cluster whose centroid is within thresholdM. */
export function clusterCells(cells: Cell[], thresholdM = 300): Site[] {
  type Acc = { network: Network; w: number; lat: number; lon: number; radios: Set<string> };
  const clusters: Acc[] = [];
  for (const c of cells) {
    const w = Math.max(1, c.samples);
    const hit = clusters.find(k => k.network === c.network && distanceM(k, c) <= thresholdM);
    if (hit) {
      hit.lat = (hit.lat * hit.w + c.lat * w) / (hit.w + w);
      hit.lon = (hit.lon * hit.w + c.lon * w) / (hit.w + w);
      hit.w += w;
      hit.radios.add(c.radio);
    } else {
      clusters.push({ network: c.network, w, lat: c.lat, lon: c.lon, radios: new Set([c.radio]) });
    }
  }
  return clusters.map(k => ({
    id: `ocid-${k.network}-${k.lat.toFixed(5)}-${k.lon.toFixed(5)}`,
    network: k.network,
    lat: k.lat,
    lon: k.lon,
    reliability: 'stimato' as const,
    radios: sortRadios(k.radios),
    source: 'OpenCelliD',
  }));
}

/** Fold estimated sites into nearby verified/manual sites of the same network. */
export function attachToVerified(estimated: Site[], verified: Site[], maxM = 400): { verified: Site[]; extra: Site[] } {
  const out = verified.map(v => ({ ...v, radios: [...v.radios] }));
  const extra: Site[] = [];
  for (const e of estimated) {
    let best: Site | undefined;
    let bestD = Infinity;
    for (const v of out) {
      if (v.network !== e.network) continue;
      const d = distanceM(v, e);
      if (d <= maxM && d < bestD) { best = v; bestD = d; }
    }
    if (best) best.radios = sortRadios([...best.radios, ...e.radios]);
    else extra.push(e);
  }
  return { verified: out, extra };
}
